const CHUNK_LOAD_ERROR_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk .+ failed|unable to preload css/i;

export const CHUNK_LOAD_RELOAD_KEY = 'crm:chunk-load-reload';

/**
 * A chunk that is still missing this soon after our own reload is a real outage, not a stale
 * deploy — stop rather than reload-loop. Long enough for a slow reload to reach the failing import.
 */
export const CHUNK_LOAD_RELOAD_COOLDOWN_MS = 30_000;

export interface ChunkLoadRecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ChunkLoadRecoveryDeps {
  storage: ChunkLoadRecoveryStorage;
  reload: () => void;
  now?: () => number;
}

export interface VitePreloadErrorLike {
  payload?: unknown;
  preventDefault: () => void;
}

let listenerInstalled = false;

function readErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }

  return '';
}

function getDefaultDeps(): ChunkLoadRecoveryDeps {
  return {
    storage: sessionStorage,
    reload: () => {
      window.location.reload();
    }
  };
}

export function isChunkLoadError(error: unknown): boolean {
  return CHUNK_LOAD_ERROR_PATTERN.test(readErrorMessage(error));
}

/**
 * Reload for stale Vite hashed chunks after a deploy. Stores when it last reloaded so a chunk that is
 * still missing right after that reload does not loop, while a later deploy in the same tab (the old
 * one-shot flag never cleared, so the second stale deploy in a tab crashed) still recovers.
 * Returns true only when a reload was triggered.
 */
export function recoverFromChunkLoadError(error: unknown, deps: ChunkLoadRecoveryDeps = getDefaultDeps()): boolean {
  if (!isChunkLoadError(error)) {
    return false;
  }

  try {
    const nowMs = (deps.now ?? Date.now)();
    const lastReloadMs = Number(deps.storage.getItem(CHUNK_LOAD_RELOAD_KEY));
    if (Number.isFinite(lastReloadMs) && nowMs - lastReloadMs < CHUNK_LOAD_RELOAD_COOLDOWN_MS) {
      return false;
    }

    deps.storage.setItem(CHUNK_LOAD_RELOAD_KEY, String(nowMs));
    deps.reload();
    return true;
  } catch {
    return false;
  }
}

export function handleVitePreloadError(event: VitePreloadErrorLike, deps: ChunkLoadRecoveryDeps = getDefaultDeps()): void {
  if (recoverFromChunkLoadError(event.payload ?? event, deps)) {
    event.preventDefault();
  }
}

/** Idempotent. Safe to call from both standalone entry and federation RemoteRoutes. */
export function installChunkLoadRecovery(deps: ChunkLoadRecoveryDeps = getDefaultDeps()): void {
  if (typeof window === 'undefined' || listenerInstalled) {
    return;
  }

  listenerInstalled = true;
  window.addEventListener('vite:preloadError', (event: Event) => {
    handleVitePreloadError(event as Event & VitePreloadErrorLike, deps);
  });
}
